import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import type { CircuitWithGeoJSON } from '../../api/circuitsService';
import { ZOOM_CIRCLE_RADIUS, ZOOM_STATION_LABEL_SIZE } from './mapZoomScaleUtils';

// ============================================================================
// Types
// ============================================================================

export interface CircuitLayerIds {
  circuitSource: string;
  circuitLineLayer: string;
  stationsSource: string;
  stationsLayer: string;
  stationsLabelsLayer: string;
  windArrowsLayer: string;
}

export interface UseCircuitLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  circuitData: CircuitWithGeoJSON | null;
  selectedEvent: EventWithCircuit | null;
  styleVersion: number;
  trackLineWidth?: number;
  ids?: CircuitLayerIds;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_IDS: CircuitLayerIds = {
  circuitSource: 'circuit',
  circuitLineLayer: 'circuit-line',
  stationsSource: 'circuit-stations',
  stationsLayer: 'circuit-stations',
  stationsLabelsLayer: 'circuit-stations-labels',
  windArrowsLayer: 'wind-arrows',
};

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to draw circuit track and weather stations on any Nowcasting map.
 *
 * Works for main, split, and PiP maps -- pass `ids` to control the
 * source/layer names used on each map instance.
 */
export function useCircuitLayer({
  mapRef,
  circuitData,
  selectedEvent,
  styleVersion,
  trackLineWidth = 3,
  ids = DEFAULT_IDS,
}: UseCircuitLayerParams): void {
  useEffect(() => {
    if (!circuitData?.geojson || !selectedEvent) return;

    let cancelled = false;
    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const drawCircuit = () => {
      if (cancelled) return;

      const map = mapRef.current;
      if (!map) return;

      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(drawCircuit, RETRY_DELAY);
        }
        return;
      }

      removeExistingLayers(map, ids);

      try {
        addCircuitLayer(map, circuitData, trackLineWidth, ids);

        if (selectedEvent.stations && selectedEvent.stations.length > 0) {
          addStationsLayer(map, selectedEvent.stations, ids);
        }
      } catch (e) {
        console.error('Error drawing circuit:', e);
      }
    };

    drawCircuit();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [circuitData, selectedEvent, styleVersion, mapRef, trackLineWidth, ids]);
}

// ============================================================================
// Helper Functions
// ============================================================================

function removeExistingLayers(map: MapboxMap, ids: CircuitLayerIds): void {
  try {
    if (map.getLayer(ids.circuitLineLayer)) {
      map.removeLayer(ids.circuitLineLayer);
    }
    if (map.getLayer(ids.stationsLayer)) {
      map.removeLayer(ids.stationsLayer);
    }
    if (map.getLayer(ids.stationsLabelsLayer)) {
      map.removeLayer(ids.stationsLabelsLayer);
    }
    if (map.getSource(ids.circuitSource)) {
      map.removeSource(ids.circuitSource);
    }
    if (map.getSource(ids.stationsSource)) {
      map.removeSource(ids.stationsSource);
    }
  } catch {
    // Ignore errors during cleanup
  }
}

function addCircuitLayer(
  map: MapboxMap,
  circuitData: CircuitWithGeoJSON,
  lineWidth: number,
  ids: CircuitLayerIds,
): void {
  map.addSource(ids.circuitSource, {
    type: 'geojson',
    data: circuitData.geojson as GeoJSON.GeoJSON,
  });

  map.addLayer({
    id: ids.circuitLineLayer,
    type: 'line',
    source: ids.circuitSource,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: {
      'line-color': '#0E447D',
      'line-width': lineWidth,
    },
  });
}

interface Station {
  lat: number;
  lon: number;
  reference?: string;
  altitude?: number;
  nearest_segment_direction?: number;
}

function addStationsLayer(map: MapboxMap, stations: Station[], ids: CircuitLayerIds): void {
  const stationsGeoJSON: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: stations.map((station) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [station.lon, station.lat],
      },
      properties: {
        reference: station.reference || 'N/A',
        altitude: station.altitude,
        nearest_segment_direction: station.nearest_segment_direction,
      },
    })),
  };

  map.addSource(ids.stationsSource, {
    type: 'geojson',
    data: stationsGeoJSON,
  });

  map.addLayer({
    id: ids.stationsLayer,
    type: 'circle',
    source: ids.stationsSource,
    paint: {
      'circle-radius': ZOOM_CIRCLE_RADIUS,
      'circle-color': '#FFA500',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  });

  map.addLayer({
    id: ids.stationsLabelsLayer,
    type: 'symbol',
    source: ids.stationsSource,
    layout: {
      'text-field': ['get', 'reference'],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-size': ZOOM_STATION_LABEL_SIZE,
      'text-offset': [0, 0],
      'text-anchor': 'center',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#FFA500',
      'text-halo-width': 4,
      'text-halo-blur': 0,
    },
  });

  if (map.getLayer(ids.windArrowsLayer)) {
    map.moveLayer(ids.windArrowsLayer, ids.stationsLayer);
  }
}
